// libraryStore.ts
//
// Tracks books the logged-in user OWNS via D2P (purchases) — distinct from
// downloadStore which tracks ad-supported KleverBookDownload records.
//
// Owned books are read ad-free, with no expiry. The PdfViewer checks
// libraryStore.ownsBook(bookId) before showing ad slots.
//
// Also handles in-app digital checkout — opens Stripe in the system browser
// (or external on Capacitor) since we deliberately don't run Stripe inside
// the iOS webview (App Store rules).

import { makeAutoObservable, runInAction } from 'mobx';
import { hitApi } from '../database/services/ApiHelper';
import { API } from '../database/apis';
import { Book } from '../types';
import { userStore } from './userStore';
import localforage from 'localforage';

export interface OwnedEntry {
  _id: string;          // UserLibrary entry id
  purchasedAt: string;
  book: Book & { ebookUrl?: string };
}

class LibraryStore {
  owned: OwnedEntry[] = [];
  loading = false;
  checkoutLoading = false;

  constructor() {
    makeAutoObservable(this);
    this.hydrate();
  }

  async hydrate() {
    try {
      const saved = await localforage.getItem<string>('owned');
      if (saved) {
        runInAction(() => {
          this.owned = JSON.parse(saved);
        });
      }
    } catch (e) {
      console.log('Owned hydration error:', e);
    }
  }

  async persist() {
    try {
      await localforage.setItem('owned', JSON.stringify(this.owned));
    } catch (e) {
      console.log('Owned persist error:', e);
    }
  }

  ownsBook(bookId: string): boolean {
    return this.owned.some((e) => e.book._id === bookId);
  }

  ownedBookEntry(bookId: string): OwnedEntry | undefined {
    return this.owned.find((e) => e.book._id === bookId);
  }

  async getOwnedLibrary() {
    if (!userStore.user || !userStore.authToken) return;
    this.loading = true;
    try {
      const res = await hitApi({
        endpoint: API.OWNED_LIBRARY,
        method: 'GET',
        token: userStore.authToken,
      });
      const items: any[] = Array.isArray(res?.owned) ? res.owned : [];
      runInAction(() => {
        this.owned = items.map((entry: any): OwnedEntry => ({
          _id: entry.id,
          purchasedAt: entry.purchasedAt,
          book: {
            _id: entry.book.id,
            title: entry.book.title,
            authorName: entry.book.author?.name ?? 'Anonymous',
            category: entry.book.category,
            categoryName: entry.book.category,
            description: entry.book.description ?? '',
            coverImage: entry.book.coverUrl,
            bookFile: entry.book.ebookUrl,
            pdf_file: entry.book.ebookUrl,
            ad_slots: [], // owned = ad-free
          },
        }));
        this.loading = false;
      });
      await this.persist();
    } catch (e) {
      runInAction(() => { this.loading = false; });
    }
  }

  /**
   * Start a digital purchase — returns the Stripe checkout URL. The caller
   * is responsible for opening it (window.location.href on web, Capacitor
   * Browser plugin on native — see Book screen).
   *
   * Returns null on failure; toast/dynamic-island feedback is the caller's job.
   */
  async startCheckout(bookId: string): Promise<{ url: string; orderId: string } | null> {
    if (!userStore.user || !userStore.authToken) return null;
    this.checkoutLoading = true;
    try {
      const res = await hitApi({
        endpoint: API.CHECKOUT_DIGITAL,
        method: 'POST',
        body: { bookId },
        token: userStore.authToken,
      });
      runInAction(() => { this.checkoutLoading = false; });
      if (!res?.checkoutUrl) return null;
      return { url: res.checkoutUrl, orderId: res.orderId };
    } catch (e: any) {
      runInAction(() => { this.checkoutLoading = false; });
      // Re-throw so the caller can surface 'already owned' as a different
      // user-facing message vs. real errors.
      throw e;
    }
  }

  async logout() {
    runInAction(() => { this.owned = []; });
    await localforage.removeItem('owned');
  }
}

export const libraryStore = new LibraryStore();
